import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, notifyCompanyUsers } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { mergeSeedDocTemplates } from '../../core/seed-doc-templates';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { Icon, InlineIcon } from '../../components/Icon';
import { listClientDocs, uploadClientDoc, resolveDocSrc } from '../../storage';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';


// ─── Compliance packages: each = 1 billing line item + many dated task templates ───
// Due-date rule per task:
//   { kind:"annual",    month, day }                 → fixed govt date each year in the package window
//   { kind:"monthly",   day, offsetMonths }          → for each period month, due = day of (month + offsetMonths)  e.g. GSTR-3B = 20th of NEXT month
//   { kind:"quarterly", months:[...], day }          → fixed quarterly govt dates
//   { kind:"offset",    anchor:"incorporation"|"package_start", days } → one-time, X days after the anchor
const COMPLIANCE_PACKAGES = [
  { id:"llp_annual", name:"LLP Annual Compliance", entityType:"LLP", freq:"yearly", tasks:[
    { key:"llp_form11", name:"LLP Form 11 (Annual Return)",            category:"ROC", due:{ kind:"annual", month:5,  day:30 }, requirementType:"none", docTemplateId:"" },
    { key:"llp_form8",  name:"LLP Form 8 (Statement of Account & Solvency)", category:"ROC", due:{ kind:"annual", month:10, day:30 }, requirementType:"form_docs", docTemplateId:"form_8_llp" },
    { key:"llp_itr",    name:"Income Tax Return (LLP)",                 category:"Income Tax", due:{ kind:"annual", month:7,  day:31 }, requirementType:"none", docTemplateId:"" },
    { key:"llp_kyc",    name:"DIR-3 KYC (Designated Partners)",        category:"MCA", due:{ kind:"annual", month:9,  day:30 }, requirementType:"form_docs", docTemplateId:"director_kyc" },
  ]},
  { id:"pvt_annual", name:"Pvt Ltd Annual Compliance", entityType:"Private Limited", freq:"yearly", tasks:[
    { key:"aoc4",   name:"AOC-4 (Financial Statements)",   category:"ROC", due:{ kind:"annual", month:10, day:30 }, requirementType:"docs", docTemplateId:"roc_annual" },
    { key:"mgt7",   name:"MGT-7 (Annual Return)",          category:"ROC", due:{ kind:"annual", month:11, day:29 }, requirementType:"none", docTemplateId:"" },
    { key:"adt1",   name:"ADT-1 (Auditor Ratification)",   category:"MCA", due:{ kind:"annual", month:10, day:14 }, requirementType:"none", docTemplateId:"" },
    { key:"kyc",    name:"DIR-3 KYC (Directors)",          category:"MCA", due:{ kind:"annual", month:9,  day:30 }, requirementType:"form_docs", docTemplateId:"director_kyc" },
    { key:"itr_co", name:"Income Tax Return (Company)",     category:"Income Tax", due:{ kind:"annual", month:10, day:31 }, requirementType:"none", docTemplateId:"" },
  ]},
  { id:"gst_monthly", name:"GST Monthly Filing", entityType:"Any", freq:"monthly", tasks:[
    { key:"gstr1",  name:"GSTR-1 (Outward Supplies)", category:"GST", due:{ kind:"monthly", day:10, offsetMonths:1 }, requirementType:"docs", docTemplateId:"gst_return" },
    { key:"gstr3b", name:"GSTR-3B (Summary + Tax)",    category:"GST", due:{ kind:"monthly", day:20, offsetMonths:1 }, requirementType:"docs", docTemplateId:"gst_return" },
  ]},
  { id:"gst_composition", name:"GST Composition Scheme", entityType:"Any", freq:"quarterly", tasks:[
    { key:"cmp08", name:"CMP-08 (Quarterly Statement-cum-Challan)", category:"GST", due:{ kind:"quarterly", months:[7,10,1,4], day:18 }, requirementType:"docs", docTemplateId:"gst_return" },
    { key:"gstr4", name:"GSTR-4 (Composition Annual Return)",       category:"GST", due:{ kind:"annual", month:6, day:30 }, requirementType:"docs", docTemplateId:"gst_return" },
  ]},
  { id:"gst_qrmp", name:"GST Quarterly (QRMP Scheme)", entityType:"Any", freq:"quarterly", tasks:[
    { key:"gstr1_q",  name:"GSTR-1 (Quarterly — QRMP)",  category:"GST", due:{ kind:"quarterly", months:[7,10,1,4], day:13 }, requirementType:"docs", docTemplateId:"gst_return" },
    { key:"gstr3b_q", name:"GSTR-3B (Quarterly — QRMP)", category:"GST", due:{ kind:"quarterly", months:[7,10,1,4], day:22 }, requirementType:"docs", docTemplateId:"gst_return" },
  ]},
  { id:"gst_annual", name:"GST Annual Return", entityType:"Any", freq:"yearly", tasks:[
    { key:"gstr9",  name:"GSTR-9 (Annual Return)",             category:"GST", due:{ kind:"annual", month:12, day:31 }, requirementType:"docs", docTemplateId:"gst_return" },
    { key:"gstr9c", name:"GSTR-9C (Reconciliation Statement)", category:"GST", due:{ kind:"annual", month:12, day:31 }, requirementType:"docs", docTemplateId:"gst_return" },
  ]},
  { id:"tds_quarterly", name:"TDS Quarterly Return", entityType:"Any", freq:"quarterly", tasks:[
    { key:"tds", name:"TDS Return Filing", category:"TDS", due:{ kind:"quarterly", months:[7,10,1,5], day:31 }, requirementType:"docs", docTemplateId:"tds_return" },
  ]},
  { id:"post_incorp", name:"Post-Incorporation Setup (one-time)", entityType:"Private Limited", freq:"once", tasks:[
    { key:"board1",  name:"First Board Meeting",              category:"Secretarial", due:{ kind:"offset", anchor:"incorporation", days:30 }, requirementType:"none", docTemplateId:"" },
    { key:"adt1_1",  name:"First Auditor Appointment (ADT-1)", category:"MCA",         due:{ kind:"offset", anchor:"incorporation", days:30 }, requirementType:"none", docTemplateId:"" },
    { key:"bank",    name:"Open Company Bank Account",         category:"Banking",     due:{ kind:"offset", anchor:"package_start", days:15 }, requirementType:"none", docTemplateId:"" },
    { key:"shares",  name:"Issue Share Certificates",          category:"Secretarial", due:{ kind:"offset", anchor:"incorporation", days:60 }, requirementType:"none", docTemplateId:"" },
    { key:"inc20a",  name:"INC-20A (Commencement of Business)", category:"MCA",        due:{ kind:"offset", anchor:"incorporation", days:180 }, requirementType:"none", docTemplateId:"" },
  ]},
];

const ymdStr = (d) => {
  const dt = (d instanceof Date) ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
};

// Compute all due dates for one task template within a package window.
// ctx = { start:Date, end:Date, incorp:Date|null }
function computeTaskDueDates(due, ctx) {
  const { start, end, incorp } = ctx;
  const out = [];
  if (due.kind === "offset") {
    const anchor = due.anchor === "incorporation" ? incorp : start;
    if (!anchor || isNaN(anchor.getTime())) return [];  // no anchor → can't schedule
    const d = new Date(anchor); d.setDate(d.getDate() + (due.days || 0));
    const s = ymdStr(d); if (s) out.push(s);
  } else if (due.kind === "annual") {
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      const d = new Date(y, due.month - 1, due.day);
      if (d >= start && d <= end) { const s = ymdStr(d); if (s) out.push(s); }
    }
  } else if (due.kind === "monthly") {
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
      const d = new Date(cur.getFullYear(), cur.getMonth() + (due.offsetMonths || 0), due.day);
      const s = ymdStr(d); if (s) out.push(s);
      cur.setMonth(cur.getMonth() + 1);
    }
  } else if (due.kind === "quarterly") {
    for (let y = start.getFullYear(); y <= end.getFullYear() + 1; y++) {
      (due.months || []).forEach(m => {
        const d = new Date(y, m - 1, due.day);
        if (d >= start && d <= end) { const s = ymdStr(d); if (s) out.push(s); }
      });
    }
  }
  return [...new Set(out)].sort();
}

// Expand a package into concrete { taskKey, title, category, dueDate } instances for one client.
function expandPackage(pkg, { start, end, incorp }) {
  const s = new Date(start);
  const e = end ? new Date(end) : new Date(s.getFullYear() + 1, s.getMonth(), s.getDate());
  const ctx = { start: s, end: e, incorp: incorp ? new Date(incorp) : null };
  const rows = [];
  pkg.tasks.forEach(t => {
    const dates = computeTaskDueDates(t.due, ctx);
    if (dates.length === 0 && t.due.kind === "offset") {
      rows.push({ taskKey: t.key, title: t.name, category: t.category, dueDate: null, needsAnchor: t.due.anchor });
    }
    dates.forEach(d => {
      const period = t.due.kind === "annual" || t.due.kind === "monthly" || t.due.kind === "quarterly"
        ? " — " + new Date(d).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
        : "";
      rows.push({ taskKey: t.key, title: t.name + period, category: t.category, dueDate: d, needsAnchor: null });
    });
  });
  return rows;
}

const RECURRING_TEMPLATES = [
  { id:"gst_monthly",    name:"GST Return Filing",           freq:"monthly",  dayOfMonth:20, category:"GST" },
  { id:"tds_quarterly",  name:"TDS Return Filing",           freq:"quarterly",months:[7,10,1,4], dayOfMonth:31, category:"TDS" },
  { id:"roc_annual",     name:"Annual ROC Filing",           freq:"yearly",   month:11, dayOfMonth:30, category:"ROC" },
  { id:"pt_monthly",     name:"Professional Tax",            freq:"monthly",  dayOfMonth:15, category:"PT" },
  { id:"mca_annual",     name:"MCA Annual Return",           freq:"yearly",   month:9,  dayOfMonth:30, category:"MCA" },
  { id:"audit_annual",   name:"Annual Audit",                freq:"yearly",   month:9,  dayOfMonth:30, category:"Audit" },
];

function RecurringTasksPage() {
  const { clients, tasks, setTasks, employees, showToast, bundles, org, userLinks, users, docTpls } = useApp();
  const [selTemplate, setSelTemplate] = useState("");
  const [selClients,  setSelClients]  = useState([]);
  const [assignTo,    setAssignTo]    = useState(employees[0]?.id||"");
  const [startDate,   setStartDate]   = useState("");
  const [endDate,     setEndDate]     = useState("");
  const [fee,         setFee]         = useState("");
  const [taskDocConfig, setTaskDocConfig] = useState({});
  const [creating,    setCreating]    = useState(false);
  const [preview,     setPreview]     = useState([]);
  // #39 filters
  const [fClient,     setFClient]     = useState("");
  const [fCategory,   setFCategory]   = useState("");
  const [fFrom,       setFFrom]       = useState("");
  const [fTo,         setFTo]         = useState("");

  // Packages = built-in library + custom recurring bundles that carry compliance tasks (authored in Bundles & Services)
  const customPackages = (bundles || [])
    .filter(b => b.isRecurring && Array.isArray(b.complianceTasks) && b.complianceTasks.length > 0)
    .map(b => ({ id: b.id, name: b.name, entityType: "Any", freq: b.recurringFreq || "yearly", tasks: b.complianceTasks, custom: true }));
  const allPackages = [...COMPLIANCE_PACKAGES, ...customPackages];
  const selPkg = allPackages.find(p => p.id === selTemplate);
  const tplCatalog = mergeSeedDocTemplates(docTpls || {});

  useEffect(() => {
    if (!selPkg) { setTaskDocConfig({}); return; }
    const cfg = {};
    selPkg.tasks.forEach(t => {
      cfg[t.key] = {
        requirementType: t.requirementType || "none",
        docTemplateId: t.docTemplateId || "",
      };
    });
    setTaskDocConfig(cfg);
  }, [selTemplate, selPkg?.id]);

  const setTaskCfg = (key, patch) => setTaskDocConfig(c => ({ ...c, [key]: { ...c[key], ...patch } }));

  // Preview tasks before creating — expand the package for each selected client
  const previewTasks = () => {
    if (!selTemplate || !selClients.length || !startDate) { showToast("Select a package, clients and start date", "error"); return; }
    const pkg = allPackages.find(p => p.id === selTemplate);
    if (!pkg) return;
    const prev = [];
    let missing = 0;
    selClients.forEach(cId => {
      const client = clients.find(c => c.id === cId);
      expandPackage(pkg, { start: startDate, end: endDate, incorp: client?.incorporationDate }).forEach(inst => {
        if (inst.dueDate === null) missing++;
        prev.push({ client: client?.name, date: inst.dueDate, title: inst.title });
      });
    });
    if (missing) showToast(`${missing} one-time task(s) need an incorporation date on the client — add it and they schedule automatically.`, "warning");
    setPreview(prev);
  };

  const create = async () => {
    if (!selTemplate||!selClients.length||!assignTo||!startDate) { showToast("Fill all required fields","error"); return; }
    const pkg = allPackages.find(p => p.id === selTemplate);
    if (!pkg) return;

    // #G21 Guard against setting up the same package twice for a client
    const dupClients = selClients.filter(cId => tasks.some(t => t.isRecurring && t.packageId === pkg.id && t.clientId === cId));
    if (dupClients.length) {
      const names = dupClients.map(cId => clients.find(c=>c.id===cId)?.name || "a client").join(", ");
      showToast(`${pkg.name} is already set up for: ${names} — skipping those.`, "warning");
    }
    const targetClients = selClients.filter(cId => !dupClients.includes(cId));
    if (targetClients.length === 0) { showToast("All selected clients already have this package.", "error"); return; }

    const newTasks = [];
    let skipped = 0;
    targetClients.forEach(cId => {
      const client = clients.find(c=>c.id===cId);
      expandPackage(pkg, { start: startDate, end: endDate, incorp: client?.incorporationDate }).forEach((inst, idx) => {
        if (inst.dueDate === null) { skipped++; return; }
        const cfg = taskDocConfig[inst.taskKey] || {};
        const reqType = cfg.requirementType || "none";
        const docTpl = cfg.docTemplateId || null;
        newTasks.push({
          id: uuid(), title: inst.title,
          clientId: cId, clientName: client?.name||"",
          invoiceId: null, assignedTo: client?.assignedTo || assignTo || null, status: "open",
          sequence: idx+1, requirementType: reqType,
          docTemplateId: docTpl,
          category: inst.category,
          isRecurring: true, recurringTemplate: pkg.id, packageId: pkg.id, freq: pkg.freq,
          startDate, endDate, dueDate: inst.dueDate, completedDate: null,
          notes: `${pkg.name}. Window: ${startDate} to ${endDate||"+1yr"}`,
        });
      });
    });
    if (newTasks.length === 0) { showToast("Nothing to schedule — check the dates / incorporation date.", "error"); return; }

    // Save all recurring tasks to DB
    for (const t of newTasks) {
      await sb.insert("tasks", {
        id: t.id, title: t.title, company_id: t.clientId, client_name: t.clientName,
        assigned_to: t.assignedTo || null, status: "open", sequence: t.sequence || 1,
        requirement_type: t.requirementType || "none",
        doc_template_id: t.docTemplateId || null,
        category: t.category, package_id: pkg.id,
        is_recurring: true, recurring_freq: pkg.freq,
        due_date: t.dueDate || null, start_date: startDate || null,
        end_date: endDate || null, notes: t.notes || "",
      });
    }
    setTasks(ts=>[...ts,...newTasks]);

    // Billing: one invoice line item per client for the whole package (fee = package fee)
    const feeNum = Number(fee) || 0;
    let invCount = 0;
    if (feeNum > 0) {
      for (const cId of targetClients) {
        const client = clients.find(c=>c.id===cId);
        const invId = uuid();
        const invNo = `FB/${new Date().getFullYear()}/R${(Date.now()+invCount).toString(36).slice(-4).toUpperCase()}`;
        try {
          await sb.createInvoice({
            id: invId,
            invoice_no: invNo,
            company_id: cId,
            client_name: client?.name || "",
            date: today(),
            due_date: startDate || null,
            status: "unpaid",
            total: feeNum,
            paid: 0,
            pending: feeNum,
            letterhead: org,
            assigned_to: client?.assignedTo || assignTo || null,
            is_recurring: true,
            recurring_freq: pkg.freq,
            line_items: [{
              name: `${pkg.name} (annual package)`,
              type: "recurring",
              sac: org?.sac || "",
              qty: 1,
              unit_price: feeNum,
              gst: false,
              amount: feeNum,
              seq: 0,
              tasks: [],
            }],
            schedule: null,
          });
          await notifyCompanyUsers(cId, { userLinks, users, clients }, {
            type: "invoice",
            title: `${pkg.name} — bill`,
            body: `A bill of ₹${feeNum.toLocaleString("en-IN")} for ${pkg.name} has been raised.`,
            meta: { invoiceId: invId, modal: "view-invoice" },
          });
          invCount++;
        } catch (e) {
          console.error("Recurring package invoice failed:", e.message);
        }
      }
    }

    showToast(`${newTasks.length} tasks created${skipped?` (${skipped} one-time skipped — no incorporation date)`:""}${invCount?` + ${invCount} invoice(s)`:""}!`,"success");
    setSelClients([]); setSelTemplate(""); setFee(""); setCreating(false); setPreview([]);
  };

  const localRecurringTasks = tasks.filter(t=>t.isRecurring).filter(t=>{
    if (fClient && t.clientId !== fClient) return false;
    if (fCategory && t.category !== fCategory) return false;
    if (!dateInRange(t.dueDate, fFrom, fTo)) return false;
    return true;
  });
  const apiFilter = buildApiFilter({
    equals: {
      is_recurring: "true",
      ...(fClient ? { company_id: fClient } : {}),
      ...(fCategory ? { category: fCategory } : {}),
    },
    from: fFrom,
    to: fTo,
    dateField: "due_date",
    order: "due_date.asc",
  });
  const { rows: recurringTasks, hasMore, loadMore, loadingMore } = usePaginatedApiRows("tasks", apiFilter, localRecurringTasks, { enabled: !!apiFilter });
  const recurCategories = [...new Set(tasks.filter(t=>t.isRecurring).map(t=>t.category).filter(Boolean))];
  const clearFilters = () => { setFClient(""); setFCategory(""); setFFrom(""); setFTo(""); };

  // Group by template
  const grouped = {};
  recurringTasks.forEach(t => {
    const key = t.recurringTemplate||"other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  });

  return (
    <>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
        <div style={{ fontSize:13,color:"var(--muted)" }}>
          Pick a compliance package — tasks are auto-created with the correct statutory due dates (and one-time deadlines off the client's incorporation date).
        </div>
        <button className="btn btn-primary" onClick={()=>setCreating(true)}>+ Set Up Recurring Tasks</button>
      </div>

      <FilterBar
        clientId={fClient} setClientId={setFClient} clients={clients}
        from={fFrom} setFrom={setFFrom} to={fTo} setTo={setFTo} onClear={clearFilters} />

      {creating && (
        <div className="card card-gold" style={{ marginBottom:20 }}>
          <div className="card-head"><div className="card-title">Configure Recurring Tasks</div></div>
          <div className="card-body">
            <div className="form-grid-2" style={{ marginBottom:16 }}>
              <div className="f-group">
                <label className="f-label">Compliance Package <span className="f-req">*</span></label>
                <select className="f-select" value={selTemplate} onChange={e=>{setSelTemplate(e.target.value);setPreview([]);}}>
                  <option value="">Select package…</option>
                  {COMPLIANCE_PACKAGES.map(p=><option key={p.id} value={p.id}>{p.name}{p.entityType!=="Any"?` (${p.entityType})`:""}</option>)}
                  {customPackages.length>0 && <optgroup label="Your custom packages">{customPackages.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
                </select>
                {selPkg && (
                  <div className="f-hint" style={{ lineHeight:1.6 }}>
                    <strong>{selPkg.tasks.length} task type{selPkg.tasks.length!==1?"s":""}</strong> · bills as 1 line item
                    <div style={{ marginTop:4 }}>
                      {selPkg.tasks.map(t=>{
                        const d=t.due;
                        const M=["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                        let rule="";
                        if (d.kind==="annual") rule=`${M[d.month]} ${d.day} yearly`;
                        else if (d.kind==="monthly") rule=`${d.day}${d.day===1?"st":d.day===2?"nd":d.day===3?"rd":"th"} of ${d.offsetMonths?"next month":"the month"}`;
                        else if (d.kind==="quarterly") rule=`${d.day}${d.day===31?"st":"th"} — ${d.months.map(m=>M[m]).join("/")}`;
                        else if (d.kind==="offset") rule=`+${d.days}d from ${d.anchor==="incorporation"?"incorporation":"start"}`;
                        return <div key={t.key} style={{ fontSize:11 }}>• {t.name} <span style={{ color:"var(--muted)" }}>— {rule}</span></div>;
                      })}
                    </div>
                    {selPkg.tasks.some(t=>t.due.kind==="offset"&&t.due.anchor==="incorporation") && <div style={{ marginTop:4, color:"var(--gold-dk)" }}><InlineIcon name="warning" size={12} gap={4}>One-time tasks need the client's incorporation date.</InlineIcon></div>}
                  </div>
                )}
              </div>
              <div className="f-group">
                <label className="f-label">Assign To <span className="f-req">*</span></label>
                <select className="f-select" value={assignTo} onChange={e=>setAssignTo(e.target.value)}>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">Package Start Date <span className="f-req">*</span></label>
                <input type="date" className="f-input" value={startDate} onChange={e=>{setStartDate(e.target.value);setPreview([]);}} />
              </div>
              <div className="f-group">
                <label className="f-label">Package End Date</label>
                <input type="date" className="f-input" value={endDate} onChange={e=>{setEndDate(e.target.value);setPreview([]);}} />
                <div className="f-hint">Leave blank for 1 year from start date</div>
              </div>
              <div className="f-group">
                <label className="f-label">Package fee (₹) — optional</label>
                <input type="number" className="f-input" value={fee} onChange={e=>setFee(e.target.value)} placeholder="e.g. 12000 — blank = tasks only" />
                <div className="f-hint">If set, each client gets one invoice with a single line item for the whole package.</div>
              </div>
              {selPkg && (
                <div className="f-group" style={{ gridColumn:"1/-1" }}>
                  <label className="f-label">Documents to collect — per task type</label>
                  <div className="tbl-wrap">
                    <table>
                      <thead><tr><th>Task</th><th>Requirement</th><th>Template</th></tr></thead>
                      <tbody>
                        {selPkg.tasks.map(t => {
                          const cfg = taskDocConfig[t.key] || { requirementType:"none", docTemplateId:"" };
                          return (
                            <tr key={t.key}>
                              <td style={{ fontSize:12, fontWeight:500 }}>{t.name}</td>
                              <td>
                                <select className="f-select" value={cfg.requirementType} onChange={e=>{
                                  const v = e.target.value;
                                  setTaskCfg(t.key, { requirementType: v, docTemplateId: v === "none" ? "" : cfg.docTemplateId });
                                }}>
                                  {TASK_REQUIREMENT_TYPES.map(rt=><option key={rt.id} value={rt.id}>{rt.label}</option>)}
                                </select>
                              </td>
                              <td>
                                {(cfg.requirementType === "docs" || cfg.requirementType === "form_docs") ? (
                                  <select className="f-select" value={cfg.docTemplateId} onChange={e=>setTaskCfg(t.key,{ docTemplateId: e.target.value })}>
                                    <option value="">Select template…</option>
                                    {Object.values(tplCatalog).map(dt=><option key={dt.id} value={dt.id}>{dt.name}</option>)}
                                  </select>
                                ) : <span style={{ fontSize:11, color:"var(--muted)" }}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="f-hint">Each task type keeps one template for all recurrences in this package.</div>
                </div>
              )}
            </div>

            <div className="f-group" style={{ marginBottom:16 }}>
              <label className="f-label">Clients <span className="f-req">*</span></label>
              <div className="client-checklist">
                {clients.map(c => {
                  const on = selClients.includes(c.id);
                  return (
                    <label key={c.id} className="client-check-row">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          setSelClients(prev => on ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                          setPreview([]);
                        }}
                      />
                      <span>{c.name} ({c.clientNo || c.id.slice(0, 8)})</span>
                    </label>
                  );
                })}
              </div>
              <div className="f-hint">Tap to select clients · {selClients.length} selected</div>
            </div>

            {/* Preview */}
            <button className="btn btn-sm" style={{ marginBottom:12 }} onClick={previewTasks}><InlineIcon name="view" size={13} gap={5}>Preview Tasks to be Created</InlineIcon></button>
            {preview.length > 0 && (
              <div style={{ maxHeight:200,overflowY:"auto",border:"1px solid var(--border)",borderRadius:8,marginBottom:16 }}>
                <div style={{ padding:"8px 14px",background:"var(--gold-lt)",borderBottom:"1px solid var(--border)",fontSize:12,fontWeight:700,color:"var(--gold-dk)" }}>
                  {preview.length} tasks will be created
                </div>
                {preview.slice(0,20).map((p,i)=>(
                  <div key={i} style={{ display:"flex",gap:12,padding:"7px 14px",borderBottom:"1px solid #F0EEE9",fontSize:12 }}>
                    <span style={{ color:"var(--muted)",minWidth:120 }}>{p.client}</span>
                    <span style={{ fontWeight:500 }}>{p.title}</span>
                    <span style={{ marginLeft:"auto",color:"var(--navy)",fontWeight:600 }}>Due: {new Date(p.date).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</span>
                  </div>
                ))}
                {preview.length > 20 && <div style={{ padding:"7px 14px",fontSize:11,color:"var(--muted)" }}>+{preview.length-20} more…</div>}
              </div>
            )}

            <div style={{ display:"flex",gap:10 }}>
              <button className="btn" onClick={()=>{setCreating(false);setPreview([]);}}>Cancel</button>
              <button className="btn btn-gold" onClick={create}>
                <InlineIcon name="check" size={14} gap={5}>Create {preview.length > 0 ? `${preview.length} Tasks` : "Tasks"} →</InlineIcon>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="stat-cards" style={{ marginBottom:20 }}>
        {[
          ["Total Recurring", recurringTasks.length, "var(--navy)"],
          ["Pending", recurringTasks.filter(t=>t.status!=="completed"&&t.status!=="cancelled").length, "var(--orange)"],
          ["Completed", recurringTasks.filter(t=>t.status==="completed").length, "var(--green)"],
          ["Overdue", recurringTasks.filter(t=>t.dueDate&&new Date(t.dueDate)<new Date()&&t.status!=="completed").length, "var(--red)"],
        ].map(([l,v,c])=>(
          <div key={l} className="stat-box" style={{ cursor:"default" }}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{ color:c,fontSize:22 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Grouped by template */}
      {Object.entries(grouped).map(([tplId, taskList]) => {
        const tpl = allPackages.find(p=>p.id===tplId) || RECURRING_TEMPLATES.find(t=>t.id===tplId);
        return (
          <div key={tplId} className="card" style={{ marginBottom:16 }}>
            <div className="card-head">
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <Icon name="repeat" size={18} />
                <div>
                  <div className="card-title">{tpl?.name||tplId}</div>
                  <div style={{ fontSize:11,color:"var(--muted)" }}>{taskList.length} tasks · {tpl?.freq}</div>
                </div>
              </div>
              <span className="tag tag-gold">{tpl?.entityType || tpl?.category || tpl?.freq}</span>
            </div>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Task</th><th>Client</th><th>Due Date</th><th>Status</th><th>Package Period</th></tr></thead>
                <tbody>
                  {taskList.map(t=>{
                    const isOverdue = t.dueDate&&new Date(t.dueDate)<new Date()&&t.status!=="completed";
                    return (
                      <tr key={t.id}>
                        <td style={{ fontWeight:500,fontSize:13 }}>{t.title}</td>
                        <td style={{ fontSize:12,color:"var(--muted)" }}>{t.clientName}</td>
                        <td>
                          <span style={{ fontSize:12,fontWeight:600,color:isOverdue?"var(--red)":"var(--ink)",display:"inline-flex",alignItems:"center",gap:4 }}>
                            {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—"}
                            {isOverdue && <Icon name="warning" size={12} />}
                          </span>
                        </td>
                        <td><Badge status={t.status}/></td>
                        <td style={{ fontSize:11,color:"var(--muted)" }}>{t.startDate||"—"} → {t.endDate||"Ongoing"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {recurringTasks.length===0 && (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Icon name="repeat" size={30} /></div>
            <div style={{ fontWeight:600,marginBottom:6 }}>No recurring tasks yet</div>
            <div style={{ fontSize:13 }}>Set up GST filing, annual ROC compliance, TDS returns etc.</div>
          </div>
        </div>
      )}

      <LoadMoreBar hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} count={recurringTasks.length} />
    </>
  );
}





export { RecurringTasksPage, computeTaskDueDates, expandPackage };
